import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AudioLines,
  BadgeEuro,
  BarChart3,
  BatteryCharging,
  BellPlus,
  ChartLine,
  ChevronsLeftRightEllipsis,
  CircleUserRound,
  DatabaseBackup,
  DollarSign,
  DraftingCompass,
  Euro,
  Grip,
  LayersIcon,
  Lightbulb,
  LineChart,
  LockKeyhole,
  Languages,
  MapPlus,
  PieChart,
  Receipt,
  ReceiptEuro,
  Rocket,
  ShieldUser,
  SmilePlus,
  Table2,
  Target,
  ThumbsDown,
  TrendingUp,
  ChartNoAxesCombined,
  Clock,
  Globe,
  UserRoundSearch,
  Users,
  VenusAndMars,
} from 'lucide-react';

/** Default template id for the Topics workspace report. */
export const TOPICS_REPORT_DEFAULT_ID = 'd9f1e2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a';
export const ROI_REPORT_DEFAULT_ID = 'b83e9571-e949-4c74-ae0d-4459ce78ba69';

/** Built-in dashboard report UUIDs → sidebar icon ( Topics, Failure Intelligence, etc. ). */
export const DEFAULT_REPORT_ICON_BY_DEFAULT_ID = {
  [TOPICS_REPORT_DEFAULT_ID]: Grip, // Topics
  '4a0d628e-45bb-4387-9833-2337a6c97374': ThumbsDown, // Failure intelligence
  '1498f637-42a3-465f-9295-dd5f521f784d': SmilePlus, // User satisfaction
  '2fb9dae1-1d84-49ea-be8f-9a2f70068f4b': Lightbulb, // Business Risks
  '4578faf8-e7c7-4bfb-b154-1880d8c633b0': TrendingUp, // AI adoption
  'b83e9571-e949-4c74-ae0d-4459ce78ba69': ChartNoAxesCombined,
} as const satisfies Record<string, LucideIcon>;

/**
 * Persisted optional `report.icon` string keys ↔ Lucide component.
 * Add new picker options here and in `REPORT_SIDEBAR_ICON_OPTIONS` only.
 */
export const REPORT_SIDEBAR_ICON_BY_KEY = {
  grip: Grip,
  thumbs_down: ThumbsDown,
  smile_plus: SmilePlus,
  lightbulb: Lightbulb,
  trending_up: TrendingUp,
  layers: LayersIcon,
  bar_chart_3: BarChart3,
  line_chart: LineChart,
  pie_chart: PieChart,
  table_2: Table2,
  activity: Activity,
  target: Target,
  dollar_sign: DollarSign,
  rocket: Rocket,
  languages: Languages,
  lock_keyhole: LockKeyhole,
  audio_lines: AudioLines,
  chart_line: ChartLine,
  chevrons_left_right_ellipsis: ChevronsLeftRightEllipsis,
  drafting_compass: DraftingCompass,
  database_backup: DatabaseBackup,
  battery_charging: BatteryCharging,
  venus_and_mars: VenusAndMars,
  bell_plus: BellPlus,
  map_plus: MapPlus,
  euro: Euro,
  badge_euro: BadgeEuro,
  receipt_euro: ReceiptEuro,
  receipt: Receipt,
  user_round_search: UserRoundSearch,
  shield_user: ShieldUser,
  circle_user_round: CircleUserRound,
  users: Users,
  globe: Globe,
  clock: Clock,
} as const satisfies Record<string, LucideIcon>;

export type SidebarReportIconKey = keyof typeof REPORT_SIDEBAR_ICON_BY_KEY;

export type ReportSidebarIconOption = {
  value: SidebarReportIconKey;
  Icon: LucideIcon;
  label: string;
};

export const REPORT_SIDEBAR_ICON_OPTIONS: ReportSidebarIconOption[] = [
  { value: 'grip', Icon: Grip, label: 'Grip' },
  { value: 'thumbs_down', Icon: ThumbsDown, label: 'Thumbs down' },
  { value: 'smile_plus', Icon: SmilePlus, label: 'Smile plus' },
  { value: 'lightbulb', Icon: Lightbulb, label: 'Lightbulb' },
  { value: 'trending_up', Icon: TrendingUp, label: 'Trending up' },
  { value: 'layers', Icon: LayersIcon, label: 'Layers' },
  { value: 'bar_chart_3', Icon: BarChart3, label: 'Bar chart' },
  { value: 'line_chart', Icon: LineChart, label: 'Line chart' },
  { value: 'pie_chart', Icon: PieChart, label: 'Pie chart' },
  { value: 'table_2', Icon: Table2, label: 'Table' },
  { value: 'activity', Icon: Activity, label: 'Activity' },
  { value: 'target', Icon: Target, label: 'Target' },
  { value: 'dollar_sign', Icon: DollarSign, label: 'Dollar sign' },
  { value: 'rocket', Icon: Rocket, label: 'Rocket' },
  { value: 'languages', Icon: Languages, label: 'Languages' },
  { value: 'lock_keyhole', Icon: LockKeyhole, label: 'Lock keyhole' },
  { value: 'audio_lines', Icon: AudioLines, label: 'Audio lines' },
  { value: 'chart_line', Icon: ChartLine, label: 'Chart line' },
  { value: 'chevrons_left_right_ellipsis', Icon: ChevronsLeftRightEllipsis, label: 'Chevrons left right ellipsis' },
  { value: 'drafting_compass', Icon: DraftingCompass, label: 'Drafting compass' },
  { value: 'database_backup', Icon: DatabaseBackup, label: 'Database backup' },
  { value: 'battery_charging', Icon: BatteryCharging, label: 'Battery charging' },
  { value: 'venus_and_mars', Icon: VenusAndMars, label: 'Venus and Mars' },
  { value: 'bell_plus', Icon: BellPlus, label: 'Bell plus' },
  { value: 'map_plus', Icon: MapPlus, label: 'Map plus' },
  { value: 'euro', Icon: Euro, label: 'Euro' },
  { value: 'badge_euro', Icon: BadgeEuro, label: 'Badge euro' },
  { value: 'receipt_euro', Icon: ReceiptEuro, label: 'Receipt euro' },
  { value: 'receipt', Icon: Receipt, label: 'Receipt' },
  { value: 'user_round_search', Icon: UserRoundSearch, label: 'User round search' },
  { value: 'shield_user', Icon: ShieldUser, label: 'Shield user' },
  { value: 'circle_user_round', Icon: CircleUserRound, label: 'Circle user round' },
  { value: 'users', Icon: Users, label: 'Users' },
  { value: 'globe', Icon: Globe, label: 'Globe' },
  { value: 'clock', Icon: Clock, label: 'Clock' },
];

export function resolveReportSidebarIcon(params: { icon?: string | null; defaultId?: string | null }): LucideIcon {
  const { icon, defaultId } = params;
  if (icon != null && icon !== '') {
    const byKey = REPORT_SIDEBAR_ICON_BY_KEY[icon as SidebarReportIconKey] ?? undefined;
    if (byKey != null) {
      return byKey;
    }
  }
  if (!defaultId) return LayersIcon;
  return DEFAULT_REPORT_ICON_BY_DEFAULT_ID[defaultId as keyof typeof DEFAULT_REPORT_ICON_BY_DEFAULT_ID] ?? LayersIcon;
}
